import { useState, useEffect } from "react";
import axios from "axios";
import { Car, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (price) => {
  if (!price) return "Op aanvraag";
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(price);
};

const CAR_ICONS = {
  BMW: "M",
  Audi: "A",
  Volkswagen: "VW",
  Honda: "H",
  Ford: "F",
  Toyota: "T",
  Nissan: "N",
  Subaru: "S",
  Mitsubishi: "M",
  Mercedes: "MB",
};

export default function RecommendedForCar({ onShopClick, onCarClick }) {
  const [data, setData] = useState([]);
  const [activeMake, setActiveMake] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products/recommended?limit=40`)
      .then((r) => {
        setData(r.data);
        if (r.data.length > 0) setActiveMake(r.data[0].make);
      })
      .catch(() => {});
  }, []);

  if (data.length === 0) return null;

  const activeData = data.find((d) => d.make === activeMake);

  return (
    <section data-testid="recommended-section" className="py-20 px-4 bg-[#111]">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              <Car size={22} className="text-[#FF3B30]" />
              <span className="text-[#FF3B30] text-xs font-bold uppercase tracking-[0.2em]">Voor jouw auto</span>
            </div>
            <h2 className="text-white text-3xl sm:text-4xl font-bold font-['Orbitron'] leading-tight">
              Aanbevolen <span className="text-[#FF3B30]">Onderdelen</span>
            </h2>
            <p className="text-[#555] mt-2 text-sm">Performance upgrades voor de populairste tuning-auto's</p>
          </div>
        </div>

        {/* Car brand tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide" data-testid="car-brand-tabs">
          {data.map((item) => (
            <button
              key={item.make}
              data-testid={`car-tab-${item.make.toLowerCase()}`}
              onClick={() => setActiveMake(item.make)}
              className={`flex-shrink-0 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all border whitespace-nowrap ${
                activeMake === item.make
                  ? "text-white border-[#FF3B30]/60 shadow-[0_0_15px_rgba(255,59,48,0.15)]"
                  : "bg-[#0A0A0A] text-[#666] border-[#1E1E1E] hover:text-white hover:border-[#333]"
              }`}
              style={activeMake === item.make ? { background: `${item.accent}22`, borderColor: item.accent } : {}}
            >
              <span className="inline-flex items-center justify-center w-6 h-6 rounded mr-2 text-[10px] font-bold"
                style={{ 
                  background: activeMake === item.make ? item.accent : '#1a1a1a',
                  color: activeMake === item.make ? '#fff' : '#555'
                }}
              >
                {CAR_ICONS[item.make] || item.make[0]}
              </span>
              {item.label}
              <span className="ml-1.5 text-[10px] opacity-60">({item.total_count})</span>
            </button>
          ))}
        </div>

        {/* Products for active car brand */}
        {activeData && (
          <div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
              {activeData.products.map((product, i) => (
                <div
                  key={product.slug || i}
                  data-testid={`recommended-product-${i}`}
                  onClick={() => onCarClick?.(product)}
                  className="group bg-[#0A0A0A] border border-[#1E1E1E] rounded-xl overflow-hidden hover:border-[#FF3B30]/40 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-square bg-white flex items-center justify-center overflow-hidden relative">
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.title}
                        className="w-full h-full object-contain p-3 group-hover:scale-[1.04] transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#1a1a1a] flex items-center justify-center">
                        <span className="text-[#333] font-bold">{product.brand}</span>
                      </div>
                    )}
                    {product.car_model && (
                      <span className="absolute bottom-2 left-2 text-[9px] font-bold bg-black/70 text-white/80 px-2 py-0.5 rounded backdrop-blur-sm line-clamp-1 max-w-[80%]">
                        {product.car_make} {product.car_model?.substring(0, 30)}
                      </span>
                    )}
                  </div>
                  <div className="p-3.5">
                    <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#FF3B30]/70 mb-0.5 block">
                      {product.brand}
                    </span>
                    <h3 className="text-white text-[12px] sm:text-[13px] font-medium leading-snug line-clamp-2 min-h-[2.4rem] mb-2">
                      {product.title}
                    </h3>
                    <span className="text-[#FF3B30] font-bold text-base">{formatPrice(product.price)}</span>
                  </div>
                </div>
              ))}
            </div>

            <button
              data-testid={`view-all-${activeMake?.toLowerCase()}`}
              onClick={() => onShopClick?.(activeMake)}
              className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#0A0A0A] border border-[#1E1E1E] rounded-xl text-white font-semibold text-sm hover:border-[#FF3B30]/40 hover:text-[#FF3B30] transition-all group"
            >
              Bekijk alle {activeData.total_count} {activeMake} producten
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
